import { useEffect, useState } from 'react';
import type { CampanhaCompleta, VerticalDaCampanha } from '../../api/cptu';
import type { Resultado } from '../../cptu/motor';
import type { VerticalDaAnalise } from '../../cptu/visual';
import { leiturasDaSessao, resultadoGravado } from './interpretacao';

/**
 * A aba Análise integrada da campanha CPTu — onda 316 (R2; spec 07 §5.7 e §11): as vistas, o endereço e as verticais
 * como a análise as vê.
 *
 * Cada vista tem rota (o molde das vistas da vertical): Mapa e seção `/cptu/:id/analise` (com `?linha=` quando a
 * linha é uma gravada, o nome codificado — `encodeURIComponent('A–B')` = `A%E2%80%93B`), Perfis
 * `/cptu/:id/analise/perfis` e, na Tarefa 316.3, o 3D `/cptu/:id/analise/3d` — o three.js e a grade só descem quando
 * alguém pede (spec §3.2, «sob demanda»).
 *
 * O `Resultado` de cada vertical é o GRAVADO (R4: campanha + exceções + edição, `resultadoGravado`), calculado na
 * página (≈ 23 ms cada) e guardado na sessão pela chave do que o muda — voltar à aba não recalcula.
 */

/** As vistas da análise (R2): Mapa e seção, Perfis e o 3D (Tarefa 316.3, passos 6–10). */
export type VistaDaAnalise = 'mapa' | 'perfis' | '3d';

/** O endereço de uma vista da análise, com a linha gravada no `?linha=` (o nome codificado). */
export function rotaDaAnalise(campanhaId: string, vista: VistaDaAnalise = 'mapa', linha?: string | null): string {
  const base = `/cptu/${campanhaId}/analise${vista === 'mapa' ? '' : `/${vista}`}`;
  return linha ? `${base}?linha=${encodeURIComponent(linha)}` : base;
}

/** O que muda o resultado gravado de uma vertical: as premissas da campanha e da vertical, o NA, a cota e a edição. */
const chaveDoResultado = (c: CampanhaCompleta, v: VerticalDaCampanha): string =>
  JSON.stringify([v.id, c.campanha.premissas ?? {}, v.premissas ?? {}, v.nivel_agua, v.cota, v.edicao ?? null]);

/** Os resultados gravados já calculados nesta sessão. */
const RESULTADOS_DA_SESSAO = new Map<string, Resultado>();

/** As verticais que a análise considera: as ativas da campanha (as substituídas e canceladas não entram). */
export const verticaisAtivas = (c: CampanhaCompleta): VerticalDaCampanha[] => c.verticais.filter((v) => v.situacao === 'ativa');

/** Um tique à parte: a tela desenha «Calculando…» entre uma vertical e outra. */
const tique = () => new Promise<void>((ok) => { setTimeout(ok, 0); });

/**
 * As verticais ativas da campanha com o `Resultado` gravado (as leituras pelo cache da sessão). `verticais` fica null
 * enquanto calcula; a falha de uma leitura vai em `erro`.
 */
export function useAnaliseDaCampanha(c: CampanhaCompleta): { verticais: VerticalDaAnalise[] | null; erro: unknown } {
  const [verticais, setVerticais] = useState<VerticalDaAnalise[] | null>(null);
  const [erro, setErro] = useState<unknown>(null);
  useEffect(() => {
    let vivo = true;
    setErro(null);
    void (async () => {
      try {
        const saida: VerticalDaAnalise[] = [];
        for (const v of verticaisAtivas(c)) {
          const chave = chaveDoResultado(c, v);
          let r = RESULTADOS_DA_SESSAO.get(chave);
          if (!r) {
            const leituras = await leiturasDaSessao(v.id);
            if (!vivo) return;
            r = resultadoGravado(c, v, leituras);
            RESULTADOS_DA_SESSAO.set(chave, r);
            await tique();
          }
          saida.push({ codigo: v.codigo, latitude: v.latitude, longitude: v.longitude, cota: v.cota, r });
        }
        if (vivo) setVerticais(saida);
      } catch (e) {
        if (vivo) setErro(e);
      }
    })();
    return () => { vivo = false; };
  }, [c]);
  return { verticais, erro };
}
